import { useGetPsiContractingProfile } from '@/lib/api/psi-profile';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useGetCollaborationContract, useSignCollaboration } from '@/lib/api/collaborations.ts';
import { CircularProgress, Box } from '@mui/material';
import { Link } from 'react-router-dom';
import { useGetMe } from '@/lib/api/me.ts';
import { getMissingContractData, getSignErrorMessage } from '@/features/user/contract-data.ts';
import useSnackbar from '@/hooks/useSnackbar.tsx';

interface CollaborationContractModalProps {
  open: boolean;
  onClose: () => void;
  collaborationId: string;
  userRole: 'investor' | 'psi';
  hasUserSigned: boolean;
  bothHaveSigned: boolean;
  hasUserSignature: boolean;
  onSignSuccess?: () => void;
}

export const CollaborationContractModal = ({
  open,
  onClose,
  collaborationId,
  userRole,
  hasUserSigned,
  hasUserSignature,
  bothHaveSigned,
  onSignSuccess,
}: CollaborationContractModalProps) => {
  const { mutate: signCollab, isPending: isSigningCollaboration } = useSignCollaboration();
  const { show: showSnackbar } = useSnackbar();

  const {
    data: contractData,
    isLoading,
    isError,
  } = useGetCollaborationContract(collaborationId, open);
  const contractUrl = contractData?.url;

  const handleDownloadPdf = () => {
    if (!contractUrl) {
      return;
    }

    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = contractUrl;
    link.download = 'contrato.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSign = () => {
    signCollab(
      { id: collaborationId, role: userRole },
      {
        onSuccess: async () => {
          onClose();
          onSignSuccess?.();
        },
        onError: (error) => {
          showSnackbar({ title: getSignErrorMessage(error), variant: 'error' });
        },
      }
    );
  };

  const { data: currentUser } = useGetMe();
  const contracting = useGetPsiContractingProfile(open && userRole === 'psi');
  const missingContractData =
    userRole === 'psi'
      ? (contracting.data?.missingFields ?? [])
      : getMissingContractData(currentUser);
  const canSign =
    hasUserSignature &&
    missingContractData.length === 0 &&
    !hasUserSigned &&
    !bothHaveSigned &&
    (userRole !== 'psi' || contracting.data?.readyToContract === true);
  const canDownload = bothHaveSigned;
  const missingSignature = !hasUserSignature;
  const missingProfileData = hasUserSignature && missingContractData.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Contrato de colaboración</DialogTitle>
      <DialogContent dividers>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
            <CircularProgress />
          </Box>
        ) : isError ? (
          <div style={{ color: 'red' }}>No se pudo cargar el contrato.</div>
        ) : contractUrl ? (
          <Box sx={{ width: '100%', height: '600px' }}>
            <iframe
              src={contractUrl}
              width="100%"
              height="100%"
              style={{ border: 'none' }}
              title="Contrato de colaboración"
            />
          </Box>
        ) : (
          <div style={{ color: 'red' }}>No hay contrato disponible.</div>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1 }}>
          {missingSignature && (
            <>
              <span style={{ color: 'red' }}>
                Debes subir tu firma en tu perfil antes de poder firmar el contrato.{' '}
              </span>
              <Link
                to={userRole === 'psi' ? '/perfil?tab=contratacion' : '/perfil'}
                style={{ textDecoration: 'underline' }}
              >
                Ir a mi perfil
              </Link>
            </>
          )}

          {missingProfileData && (
            <>
              <span style={{ color: 'red' }}>
                {`Completa estos datos en tu perfil antes de poder firmar el contrato: ${missingContractData.join(', ')}. `}
              </span>
              <Link
                to={userRole === 'psi' ? '/perfil?tab=contratacion' : '/perfil'}
                style={{ textDecoration: 'underline' }}
              >
                Ir a mi perfil
              </Link>
            </>
          )}

          {canSign && (
            <Button
              variant="contained"
              color="success"
              onClick={handleSign}
              disabled={isSigningCollaboration}
            >
              Firmar condiciones
            </Button>
          )}

          {canDownload && (
            <Button variant="contained" color="primary" onClick={handleDownloadPdf}>
              Descargar PDF
            </Button>
          )}
        </div>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};
